import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import dayjs from "dayjs";
import { InboxIcon } from "@heroicons/react/outline";

import AccountingWrapper from "../../../../../Components/v2/Accounting/AccountingWrapper";
import AccountingPageHeader from "../../../../../Components/v2/Accounting/AccountingPageHeader";
import FormDateRange from "../../../../../Components/v2/Form/FormDateRange";
import ErrorStateTable from "../../../../../Components/ErrorStateTable";
import {
  requestAccountingSummary,
  useAccountingDispatch,
  useAccountingState,
  useAuthState,
} from "../../../../../Context";
import { cancelAPIRequest } from "../../../../../Utils";
import { wibDateRange, wibToday } from "../../../../../Utils/PeriodUtils";

const PIE_COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#F43F5E"];

const SkeletonBarChart = () => {
  return (
    <div className="w-full h-80 bg-gray-100 animate-pulse flex flex-col justify-between p-4 rounded-lg">
      <div className="flex items-end h-full space-x-2 w-full justify-around">
        {[...Array(7)].map((_, i) => (
          <div
            key={i}
            className="w-8 bg-gray-200 rounded-md"
            style={{ height: `${20 + i * 10}%` }}
          ></div>
        ))}
      </div>
    </div>
  );
};

const SkeletonPieChart = () => {
  return (
    <div className="flex bg-gray-100 justify-center items-center w-full h-80 rounded-lg">
      <div className="relative w-40 h-40 animate-pulse">
        <div className="w-40 h-40 rounded-full bg-gray-200"></div>
      </div>
    </div>
  );
};

const ProfitLossChart = ({ data, isLoading, t }) => {
  if (isLoading) return <SkeletonBarChart />;
  return (
    <div className="w-full h-80">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data}>
          <defs>
            <linearGradient id="colorGross" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.8} />
              <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="colorNet" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.8} />
              <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid
            strokeDasharray="3 3"
            vertical={false}
            stroke="#E5E7EB"
          />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 12, fill: "#6B7280" }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(value) => dayjs(value).format("MMM YYYY")}
          />
          <YAxis
            tick={{ fontSize: 12, fill: "#6B7280" }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip
            contentStyle={{
              borderRadius: "8px",
              border: "none",
              boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
            }}
            itemStyle={{ fontSize: "12px" }}
            labelFormatter={(value) => dayjs(value).format("MMM YYYY")}
          />
          <Legend wrapperStyle={{ fontSize: "12px", paddingTop: "10px" }} />
          <Area
            type="monotone"
            dataKey="grossProfit"
            name={t("accounting.summary.grossProfit")}
            stroke="#8B5CF6"
            fillOpacity={1}
            fill="url(#colorGross)"
          />
          <Area
            type="monotone"
            dataKey="netProfit"
            name={t("accounting.summary.netIncome")}
            stroke="#3B82F6"
            fillOpacity={1}
            fill="url(#colorNet)"
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

const CashFlowChart = ({ data, isLoading, t }) => {
  if (isLoading) return <SkeletonBarChart />;
  return (
    <div className="w-full h-80">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data}>
          <CartesianGrid
            strokeDasharray="3 3"
            vertical={false}
            stroke="#E5E7EB"
          />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 12, fill: "#6B7280" }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(value) => dayjs(value).format("MMM YYYY")}
          />
          <YAxis
            tick={{ fontSize: 12, fill: "#6B7280" }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip
            cursor={{ fill: "transparent" }}
            contentStyle={{
              borderRadius: "8px",
              border: "none",
              boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
            }}
            labelFormatter={(value) => dayjs(value).format("MMM YYYY")}
          />
          <Legend wrapperStyle={{ fontSize: "12px", paddingTop: "10px" }} />
          <Bar
            dataKey="inflow"
            name={t("accounting.summary.in")}
            fill="#10B981"
            radius={[4, 4, 0, 0]}
            maxBarSize={50}
          />
          <Bar
            dataKey="outflow"
            name={t("accounting.summary.out")}
            fill="#EF4444"
            radius={[4, 4, 0, 0]}
            maxBarSize={50}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

const ReceivablePayableChart = ({ data, isLoading, t }) => {
  if (isLoading) return <SkeletonBarChart />;
  return (
    <div className="w-full h-80">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data}>
          <CartesianGrid
            strokeDasharray="3 3"
            vertical={false}
            stroke="#E5E7EB"
          />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 12, fill: "#6B7280" }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(value) => dayjs(value).format("MMM YYYY")}
          />
          <YAxis
            tick={{ fontSize: 12, fill: "#6B7280" }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip
            cursor={{ fill: "transparent" }}
            contentStyle={{
              borderRadius: "8px",
              border: "none",
              boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
            }}
            labelFormatter={(value) => dayjs(value).format("MMM YYYY")}
          />
          <Legend wrapperStyle={{ fontSize: "12px", paddingTop: "10px" }} />
          <Bar
            dataKey="receivable"
            name={t("accounting.summary.receivable")}
            fill="#3B82F6"
            radius={[4, 4, 0, 0]}
            maxBarSize={50}
          />
          <Bar
            dataKey="payable"
            name={t("accounting.summary.payable")}
            fill="#EAB308"
            radius={[4, 4, 0, 0]}
            maxBarSize={50}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

const renderCustomizedLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
}) => {
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="white"
      textAnchor={x > cx ? "start" : "end"}
      dominantBaseline="central"
      fontSize="12px"
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

const ExpensePieChart = ({ data, isLoading }) => {
  const safeData = Array.isArray(data) ? data : [];
  if (isLoading) return <SkeletonPieChart />;
  return (
    <div className="w-full h-80">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={safeData}
            dataKey="total"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius={60}
            outerRadius={100}
            fill="#8884d8"
            paddingAngle={5}
            cornerRadius={5}
            label={renderCustomizedLabel}
            labelLine={false}
          >
            {safeData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={PIE_COLORS[index % PIE_COLORS.length]}
              />
            ))}
          </Pie>
          <Tooltip
            contentStyle={{
              borderRadius: "8px",
              border: "none",
              boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
            }}
          />
          <Legend wrapperStyle={{ fontSize: "12px" }} />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

const AccountingSummary = () => {
  const { t } = useTranslation();
  const accountingDispatch = useAccountingDispatch();
  const { user } = useAuthState();
  const {
    accountingSummary,
    accountingSummaryError,
    accountingSummaryLoading,
    request,
  } = useAccountingState();

  const [dateRange, setDateRange] = useState(() => {
    const today = wibToday();
    return [new Date(today.getFullYear(), 0, 1), today];
  });
  const [startDate, endDate] = dateRange;

  useEffect(() => {
    return () => {
      if (request) {
        cancelAPIRequest(request);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSummary = () => {
    if (!user?.mainBusiness?.id) return;
    // Don't fetch if only one date is selected (incomplete range)
    if (!startDate || !endDate) return;

    requestAccountingSummary(accountingDispatch, {
      businessID: user.mainBusiness.id,
      // The end is the close of that WIB day: the bare picker date is its
      // midnight and left the last day of the range out.
      ...wibDateRange(startDate, endDate),
    });
  };

  useEffect(() => {
    loadSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, user?.mainBusiness?.id, accountingDispatch]);

  function dataMapping(data = []) {
    return data
      .map((item) => ({
        date: item.date,
        ...item.values,
      }))
      .sort((a, b) => {
        return new Date(a.date + "-01") - new Date(b.date + "-01");
      });
  }

  return (
    <AccountingWrapper>
      <main className="lg:col-span-9">
        <AccountingPageHeader title={t("Summary")} />

        <div className="px-4 py-5 sm:px-6 lg:px-8">
          {/* Filters */}
          <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormDateRange
              startDate={startDate}
              endDate={endDate}
              onChange={(update) => {
                setDateRange(update);
              }}
              placeholderText={t("accounting.transactions.filter.dateRange")}
              onClear={() => setDateRange([null, null])}
              disabled={accountingSummaryLoading}
            />
          </div>

          {/* Content */}
          {!startDate && !endDate ? (
            <div className="flex flex-col justify-center items-center w-full py-24 rounded-md border border-dashed border-gray-300 bg-gray-50">
              <InboxIcon className="mx-auto h-12 w-12 text-gray-400" />
              <p className="mt-2 text-sm text-gray-500">
                {t("accounting.summary.selectDateRange")}
              </p>
            </div>
          ) : accountingSummaryError ? (
            <ErrorStateTable
              asRow={false}
              otherError={accountingSummaryError}
              onRetry={loadSummary}
            />
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Income Statement */}
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                <h2 className="text-lg font-medium text-gray-900 mb-4">
                  {t("accounting.summary.incomeStatement")}
                </h2>
                {accountingSummary && (
                  <ProfitLossChart
                    data={dataMapping(accountingSummary.profitLoss)}
                    isLoading={accountingSummaryLoading}
                    t={t}
                  />
                )}
              </div>

              {/* Cash Flow */}
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                <h2 className="text-lg font-medium text-gray-900 mb-4">
                  {t("accounting.summary.cashBankAccounts")}
                </h2>
                {accountingSummary && (
                  <CashFlowChart
                    data={dataMapping(accountingSummary.cashflow)}
                    isLoading={accountingSummaryLoading}
                    t={t}
                  />
                )}
              </div>

              {/* Payable & Receivable */}
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                <h2 className="text-lg font-medium text-gray-900 mb-4">
                  {t("accounting.summary.payableReceivable")}
                </h2>
                {accountingSummary && (
                  <ReceivablePayableChart
                    data={dataMapping(accountingSummary.receivablePayable)}
                    isLoading={accountingSummaryLoading}
                    t={t}
                  />
                )}
              </div>

              {/* Last Month Expenses */}
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                <h2 className="text-lg font-medium text-gray-900 mb-4">
                  {t("accounting.summary.lastMonthExpenditures")}
                </h2>
                {accountingSummaryLoading ? (
                  <SkeletonPieChart />
                ) : accountingSummary?.lastMonthExpenses?.length > 0 ? (
                  <ExpensePieChart
                    data={accountingSummary.lastMonthExpenses}
                    isLoading={accountingSummaryLoading}
                  />
                ) : (
                  <div className="flex flex-col justify-center items-center w-full h-80 rounded-md border border-dashed border-gray-300 bg-gray-50">
                    <InboxIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <p className="mt-2 text-sm text-gray-500">
                      {t("accounting.summary.noExpenditures")}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </main>
    </AccountingWrapper>
  );
};

export default AccountingSummary;
